import React, {useEffect, useMemo, useState} from 'react';
import type {GuessModelV1} from '@albert-io/models';
import {SupplementChip} from 'client/components/QuestionTypes/shared/DraggableChip';
import {useShallow} from 'zustand/react/shallow';
import type {DragCancelEvent, DragEndEvent, DragOverEvent, DragStartEvent} from '@dnd-kit/core';
import DndContextWrapper, {
  isKeyboardPickup
} from 'client/components/QuestionTypes/shared/DndContextWrapper';
import {useQuestionStore} from 'client/components/QuestionTypes/V2/useQuestionStores';
import useQuestionAndDrafts from '../../V2/useQuestionAndDrafts.hook';
import ItemList from './ItemList.react';
import CorrectAnswerSection from './CorrectAnswerSection.react';
import {AnswerFieldset} from '../../shared/AnswerFieldset';
import {useItemAreaCopy} from '../../shared/itemAreaCopy';
import {useItemMoveCopy} from '../../shared/itemMoveCopy';
import {useRestoreItemFocus} from '../../shared/useRestoreItemFocus';
import {dividerIndexFrom, orderingAnnouncements} from './announcements';
import {useInsertPointLabel} from './useInsertPointLabel';
interface Props {
  question: Record<string, any>;
  isGuessSubmitted?: boolean;
  guess?: GuessModelV1 | null;
}

const OrderingBankQuestion = ({question, isGuessSubmitted, guess}: Props) => {
  const {initialize, saveDraftGuess, addGuessInfo} = useQuestionAndDrafts(question);

  const useStoreHook = useQuestionStore<'ordering'>(question);

  const {
    questionInStore,
    items,
    setIsDragging,
    setActiveItemId,
    setHoveredDividerId,
    moveItemToPosition,
    setDraggedItemId,
    setPreviewTargetIndex,
    isSubmitted
    // oxlint-disable-next-line react/hooks -- useStoreHook comes from setUpQuestionStore's registry (cached zustand hook keyed by store name); its identity is stable for a mounted component
  } = useStoreHook(
    useShallow((state) => ({
      questionInStore: state.question,
      items: state.items,
      setIsDragging: state.setIsDragging,
      setActiveItemId: state.setActiveItem,
      setHoveredDividerId: state.setHoveredDividerId,
      moveItemToPosition: state.moveItemToPosition,
      setDraggedItemId: state.setDraggedItemId,
      setPreviewTargetIndex: state.setPreviewTargetIndex,
      isSubmitted: state.isSubmitted
    }))
  );

  const restoreFocusTo = useRestoreItemFocus();

  const onDragStart = (event: DragStartEvent) => {
    setIsDragging(true);
    setActiveItemId(isKeyboardPickup(event) ? (event.active.id as string) : null);
    setDraggedItemId(event.active.id as string);
  };

  const onDragOver = (event: DragOverEvent) => {
    const targetIndex = dividerIndexFrom(event.over?.id);
    setHoveredDividerId(targetIndex === null ? null : `divider-${targetIndex}`);
    setPreviewTargetIndex(targetIndex);
  };

  const endDrag = () => {
    setIsDragging(false);
    setHoveredDividerId(null);
    setDraggedItemId(null);
    setPreviewTargetIndex(null);
  };

  const onDragCancel = (_event: DragCancelEvent, {tabbedAway}: {tabbedAway: boolean}) => {
    endDrag();
    if (!tabbedAway) setActiveItemId(null);
  };

  const onDrop = (event: DragEndEvent) => {
    const {active, over} = event;
    endDrag();
    setActiveItemId(null);
    restoreFocusTo(active.id as string);

    const targetIndex = dividerIndexFrom(over?.id);
    if (targetIndex === null) return;

    moveItemToPosition(active.id as string, targetIndex);
    saveDraftGuess();
  };

  const [dragProgress] = useState({hasLeftOrigin: false});
  const insertPointLabel = useInsertPointLabel(items);
  const copy = useItemMoveCopy();
  const areaCopy = useItemAreaCopy();
  const announcements = useMemo(
    () => orderingAnnouncements(copy, items, dragProgress, insertPointLabel),
    [copy, items, dragProgress, insertPointLabel]
  );

  useEffect(() => {
    if (question && question.size > 0) {
      if (isGuessSubmitted && guess) {
        addGuessInfo(guess);
      } else {
        // Explicitly clear any stale submission state from a previously viewed question
        addGuessInfo(null);
      }
      initialize(question);
    }
  }, [question, initialize, isGuessSubmitted, guess, addGuessInfo]);

  if (!questionInStore) return null;

  return (
    <DndContextWrapper
      keyboardSelects
      onDragStart={onDragStart}
      onDragOver={onDragOver}
      onDragEnd={onDrop}
      onDragCancel={onDragCancel}
      renderOverlay={(activeId) => {
        const item = items.find((i) => i.id === activeId);
        return item ? <SupplementChip value={item.value} size='small' /> : null;
      }}
      accessibility={{announcements}}
    >
      <div className='flex flex-col'>
        <AnswerFieldset instruction={areaCopy.orderingInstruction} isSubmitted={isSubmitted}>
          <ItemList question={questionInStore} />
        </AnswerFieldset>
        {isSubmitted && <CorrectAnswerSection question={questionInStore} />}
      </div>
    </DndContextWrapper>
  );
};

export default OrderingBankQuestion;
